import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Check, X, Zap, Users, Crown, ArrowLeft, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);

  const handleUpgrade = async (packageId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setLoading(packageId);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/payments/checkout/session`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      // Open in new tab to avoid iframe restrictions (Stripe blocks iframe embedding)
      const newTab = window.open(data.url, '_blank');
      if (!newTab) {
        // Fallback if popup blocked — try top-level redirect
        window.top.location.href = data.url;
      }
      setLoading(null);
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to create checkout session. Please try again.');
      setLoading(null);
    }
  };

  const currentTier = user?.subscription_tier || 'free';

  const isCurrent = (packageId) => {
    if (currentTier === 'pro' && packageId.startsWith('pro')) return true;
    if (currentTier === 'business' && packageId.startsWith('business')) return true;
    if (currentTier === 'founding' && packageId === 'founding_member') return true;
    return false;
  };

  const proId = isAnnual ? 'pro_yearly' : 'pro_monthly';
  const bizId = isAnnual ? 'business_yearly' : 'business_monthly';

  return (
    <div className="min-h-screen bg-[var(--main-bg)] text-[var(--text-primary)]">
      {/* Header */}
      <div className="border-b-2 border-[var(--border-default)] bg-[var(--surface)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            data-testid="pricing-back-btn"
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        {/* Title */}
        <div className="mb-10 sm:mb-14">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            PRICING
          </h1>
          <p className="text-base sm:text-lg text-[var(--text-secondary)] mt-3 max-w-lg">
            Pick a plan. Lock in your vault.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center gap-4 mb-10">
          <span
            className={`text-sm font-semibold cursor-pointer transition-colors ${
              !isAnnual ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
            }`}
            onClick={() => setIsAnnual(false)}
          >
            Monthly
          </span>
          <button
            data-testid="billing-toggle"
            onClick={() => setIsAnnual(!isAnnual)}
            className="relative w-14 h-7 border-2 border-[var(--border-default)] bg-[var(--surface-alt)] transition-colors"
            aria-label="Toggle annual billing"
          >
            <div
              className={`absolute top-0.5 w-5 h-5 bg-[var(--text-primary)] transition-all duration-200 ${
                isAnnual ? 'left-[calc(100%-22px)]' : 'left-0.5'
              }`}
            />
          </button>
          <span
            className={`text-sm font-semibold cursor-pointer transition-colors ${
              isAnnual ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
            }`}
            onClick={() => setIsAnnual(true)}
          >
            Annual
          </span>
          {isAnnual && (
            <span className="text-xs font-bold bg-[var(--warning)] text-[#0A0A0A] px-2 py-0.5 border border-[var(--border-default)]">
              SAVE UP TO $41
            </span>
          )}
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-[var(--border-default)] mb-12">
          {/* FREE */}
          <div className="bg-[var(--surface)] p-6 sm:p-8 border-b-2 md:border-b-0 md:border-r-2 border-[var(--border-default)] flex flex-col">
            <div className="mb-6">
              <p className="text-xs font-bold text-[var(--text-muted)] tracking-widest uppercase mb-2">Free</p>
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>$0</span>
                <span className="text-sm text-[var(--text-muted)]">/forever</span>
              </div>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              <FeatureItem>Unlimited ideas</FeatureItem>
              <FeatureItem>20 AI enhancements / month</FeatureItem>
              <FeatureItem>Basic categories & tags</FeatureItem>
              <FeatureItem>Search & archive</FeatureItem>
            </ul>
            {user && currentTier === 'free' ? (
              <CurrentPlanBadge />
            ) : !user ? (
              <PlanButton
                testId="start-free-btn"
                loading={false}
                onClick={() => navigate('/register')}
                className="bg-[var(--surface-alt)] text-[var(--text-primary)] border-[var(--border-default)]"
              >
                Start Free
              </PlanButton>
            ) : (
              <div className="text-center text-xs text-[var(--text-muted)] py-3">Included</div>
            )}
          </div>

          {/* PRO */}
          <div className="bg-[var(--surface)] p-6 sm:p-8 border-b-2 md:border-b-0 md:border-r-2 border-[var(--border-default)] flex flex-col relative">
            <div className="absolute -top-[2px] left-0 right-0 h-1 bg-[var(--secondary)]" />
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Zap size={14} className="text-[var(--secondary)]" />
                <p className="text-xs font-bold text-[var(--secondary)] tracking-widest uppercase">Pro</p>
              </div>
              <div className="flex items-baseline gap-1">
                {isAnnual ? (
                  <>
                    <span className="text-5xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>$49</span>
                    <span className="text-sm text-[var(--text-muted)]">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>$4.99</span>
                    <span className="text-sm text-[var(--text-muted)]">/month</span>
                  </>
                )}
              </div>
              {isAnnual && (
                <p className="text-xs text-[var(--text-muted)] mt-1">~$4.08/mo &middot; save $10.88/yr</p>
              )}
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              <FeatureItem accent="var(--secondary)">Unlimited ideas</FeatureItem>
              <FeatureItem accent="var(--secondary)">Unlimited AI enhancements</FeatureItem>
              <FeatureItem accent="var(--secondary)">All 6 templates</FeatureItem>
              <FeatureItem accent="var(--secondary)">Advanced analytics</FeatureItem>
              <FeatureItem accent="var(--secondary)">Export (JSON & Markdown)</FeatureItem>
              <FeatureItem accent="var(--secondary)">Priority support</FeatureItem>
            </ul>
            {isCurrent(proId) ? (
              <CurrentPlanBadge />
            ) : (
              <PlanButton
                testId={`upgrade-${proId}-btn`}
                loading={loading === proId}
                onClick={() => handleUpgrade(proId)}
                className="bg-[var(--secondary)] text-white border-[var(--border-default)]"
              >
                {isAnnual ? 'Go Pro Yearly' : 'Go Pro Monthly'}
              </PlanButton>
            )}
          </div>

          {/* BUSINESS */}
          <div className="bg-[var(--surface)] p-6 sm:p-8 flex flex-col relative">
            <div className="absolute -top-[2px] left-0 right-0 h-1 bg-[var(--primary)]" />
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Users size={14} className="text-[var(--primary)]" />
                <p className="text-xs font-bold text-[var(--primary)] tracking-widest uppercase">Business</p>
              </div>
              <div className="flex items-baseline gap-1">
                {isAnnual ? (
                  <>
                    <span className="text-5xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>$199</span>
                    <span className="text-sm text-[var(--text-muted)]">/year</span>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>$19.99</span>
                    <span className="text-sm text-[var(--text-muted)]">/month</span>
                  </>
                )}
              </div>
              {isAnnual && (
                <p className="text-xs text-[var(--text-muted)] mt-1">~$16.58/mo &middot; save $40.88/yr</p>
              )}
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              <FeatureItem accent="var(--primary)">Everything in Pro</FeatureItem>
              <FeatureItem accent="var(--primary)">Team collaboration</FeatureItem>
              <FeatureItem accent="var(--primary)">Admin dashboard</FeatureItem>
              <FeatureItem accent="var(--primary)">API access</FeatureItem>
              <FeatureItem accent="var(--primary)">Custom branding</FeatureItem>
              <FeatureItem accent="var(--primary)">Dedicated support</FeatureItem>
            </ul>
            {isCurrent(bizId) ? (
              <CurrentPlanBadge />
            ) : (
              <PlanButton
                testId={`upgrade-${bizId}-btn`}
                loading={loading === bizId}
                onClick={() => handleUpgrade(bizId)}
                className="bg-[var(--primary)] text-white border-[var(--border-default)]"
              >
                {isAnnual ? 'Go Business Yearly' : 'Go Business Monthly'}
              </PlanButton>
            )}
          </div>
        </div>

        {/* Founding Member Banner */}
        <div className="border-2 border-[var(--border-default)] bg-[var(--surface)] relative overflow-hidden mb-16">
          <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-[var(--warning)]" />
          <div className="p-6 sm:p-8 sm:flex sm:items-center sm:justify-between gap-8">
            <div className="mb-6 sm:mb-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Crown size={16} className="text-[var(--warning)]" />
                <span className="text-xs font-bold text-[var(--warning)] tracking-widest uppercase">Limited</span>
              </div>
              <h2
                className="text-2xl sm:text-3xl font-black tracking-tight"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Founding Member &mdash; $49 Lifetime
              </h2>
              <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md">
                Lock in lifetime Pro access. Limited to the first 500 members. One payment, no renewals.
              </p>
            </div>
            <div className="shrink-0">
              {currentTier === 'founding' ? (
                <CurrentPlanBadge />
              ) : (
                <PlanButton
                  testId="upgrade-founding-btn"
                  loading={loading === 'founding_member'}
                  onClick={() => handleUpgrade('founding_member')}
                  className="bg-[var(--warning)] text-[#0A0A0A] border-[var(--border-default)]"
                >
                  Become a Founder
                </PlanButton>
              )}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-12" data-testid="comparison-table">
          <h2
            className="text-2xl sm:text-3xl font-black tracking-tight mb-8"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            COMPARE PLANS
          </h2>
          <div className="border-2 border-[var(--border-default)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[var(--border-default)] bg-[var(--surface-alt)]">
                  <th className="text-left p-4 font-bold text-[var(--text-secondary)] w-[40%]">Feature</th>
                  <th className="text-center p-4 font-bold text-[var(--text-muted)] w-[20%]">Free</th>
                  <th className="text-center p-4 font-bold text-[var(--secondary)] w-[20%]">Pro</th>
                  <th className="text-center p-4 font-bold text-[var(--primary)] w-[20%]">Business</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_DATA.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-b border-[var(--border-subtle)] ${
                      row.section ? 'bg-[var(--surface-alt)]' : 'bg-[var(--surface)]'
                    }`}
                  >
                    {row.section ? (
                      <td colSpan={4} className="p-3 px-4 font-bold text-xs tracking-widest uppercase text-[var(--text-muted)]">
                        {row.section}
                      </td>
                    ) : (
                      <>
                        <td className="p-3 px-4 text-[var(--text-primary)]">{row.feature}</td>
                        <td className="p-3 text-center"><CellValue value={row.free} /></td>
                        <td className="p-3 text-center"><CellValue value={row.pro} accent="var(--secondary)" /></td>
                        <td className="p-3 text-center"><CellValue value={row.business} accent="var(--primary)" /></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ strip */}
        <div className="text-center">
          <p className="text-sm text-[var(--text-muted)]">
            Questions?{' '}
            <button
              onClick={() => navigate('/support')}
              className="underline text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              data-testid="pricing-faq-link"
            >
              Visit our FAQ
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

/* ---- Comparison Data ---- */
const COMPARISON_DATA = [
  { section: 'Ideas' },
  { feature: 'Total ideas',          free: 'Unlimited', pro: 'Unlimited',  business: 'Unlimited' },
  { feature: 'Categories & tags',    free: true,        pro: true,         business: true },
  { feature: 'Search & archive',     free: true,        pro: true,         business: true },
  { feature: 'Daily idea prompt',    free: true,        pro: true,         business: true },
  { feature: 'Idea templates',       free: '2',         pro: 'All 6',      business: 'All 6' },
  { section: 'AI' },
  { feature: 'AI enhancements',      free: '20/month',  pro: 'Unlimited',  business: 'Unlimited' },
  { feature: 'AI content generation', free: false,      pro: true,         business: true },
  { section: 'Productivity' },
  { feature: 'Export (JSON)',         free: false,       pro: true,         business: true },
  { feature: 'Export (Markdown)',     free: false,       pro: true,         business: true },
  { feature: 'Advanced analytics',   free: false,       pro: true,         business: true },
  { feature: 'Visual stats dashboard', free: false,     pro: true,         business: true },
  { section: 'Collaboration' },
  { feature: 'Team workspaces',      free: false,       pro: false,        business: true },
  { feature: 'Shared idea boards',   free: false,       pro: false,        business: true },
  { feature: 'Team activity feed',   free: false,       pro: false,        business: true },
  { feature: 'Role-based access',    free: false,       pro: false,        business: true },
  { section: 'Admin & Security' },
  { feature: 'Two-factor auth (2FA)', free: true,       pro: true,         business: true },
  { feature: 'Admin dashboard',      free: false,       pro: false,        business: true },
  { feature: 'API access',           free: false,       pro: false,        business: true },
  { feature: 'Custom branding',      free: false,       pro: false,        business: true },
  { section: 'Support' },
  { feature: 'Community FAQ',        free: true,        pro: true,         business: true },
  { feature: 'Support tickets',      free: false,       pro: true,         business: true },
  { feature: 'Priority support',     free: false,       pro: true,         business: true },
  { feature: 'Dedicated support',    free: false,       pro: false,        business: true },
];

/* ---- Sub-components ---- */

const CellValue = ({ value, accent }) => {
  if (value === true) return <Check size={16} className="inline-block" style={{ color: accent || 'var(--text-muted)' }} />;
  if (value === false) return <X size={16} className="inline-block text-[var(--text-muted)] opacity-30" />;
  return <span className="font-medium" style={{ color: accent || 'var(--text-secondary)' }}>{value}</span>;
};

const FeatureItem = ({ children, accent }) => (
  <li className="flex items-start gap-2.5 text-sm">
    <Check size={15} className="mt-0.5 shrink-0" style={{ color: accent || 'var(--text-muted)' }} />
    <span>{children}</span>
  </li>
);

const CurrentPlanBadge = () => (
  <div
    data-testid="current-plan-badge"
    className="text-center text-xs font-bold text-[var(--text-muted)] tracking-widest uppercase py-3 border-2 border-[var(--border-subtle)] bg-[var(--surface-alt)]"
  >
    Current Plan
  </div>
);

const PlanButton = ({ children, testId, loading, onClick, className }) => (
  <button
    data-testid={testId}
    disabled={loading}
    onClick={onClick}
    className={`w-full font-bold text-sm px-6 py-3 border-2 transition-all hover:translate-y-[-1px] active:translate-y-[1px] disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 ${className}`}
  >
    {loading ? (
      <>
        <Loader size={14} className="animate-spin" /> Processing&hellip;
      </>
    ) : (
      children
    )}
  </button>
);

export default PricingPage;
